import React, { useState, forwardRef, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import RollingNumber from '../Loaders/RollingNumber';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';
import { getStarIcon } from '../../utils/starRating';

// 🔄 SVGs moved to Cloudflare R2
const Feefo =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/feefo.svg';
const Google =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/google.svg';
const HelloPeter =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/hellopeter.svg';
const BuyIconPink = 'https://assets.askhippo.co.za/Icons/common/Union_Pink.svg';

const MobileProviderTileComponent = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      category,
      product,
      buyNowLoading,
      buyNowLoadingForQuotePk,
      bestDealContentReady = true,
      wasLoadingOnMount = useRef(!bestDealContentReady),
    },
    ref
  ) => {
    const { FriendlyName, LogoURL, Excess, TotalPremium, CoverValue } = data;

    const reviewData = [
      {
        name: 'Feefo',
        score: parseFloat(data.FeefoScore.replace(',', '.')), // e.g. 4.5
        logo: Feefo,
      },
      {
        name: 'HelloPeter',
        score: parseFloat(data.HelloPeterScore.replace(',', '.')),
        logo: Google,
      },
      {
        name: 'Google',
        score: parseFloat(data.GoogleScore.replace(',', '.')),
        logo: HelloPeter,
      },
    ];
    const feefo = parseFloat(data.FeefoScore.replace(',', '.'));
    const helloPeter = parseFloat(data.HelloPeterScore.replace(',', '.'));
    const google = parseFloat(data.GoogleScore.replace(',', '.'));
    const averageRating = (feefo + helloPeter + google) / 3;

    const [isExpanded, setIsExpanded] = useState(false);

    const isBestDealLoading = data.bestDeal && !bestDealContentReady;
    const hasBuyNow = Boolean(data.OnlinePremium);

    const coverageMap = {
      HailDamage: 'Hail Damage',
      AccidentalDamage: 'Accidental Damage',
      TheftAndHijacking: 'Theft and Hijacking',
      RoadsideAssistance: 'Roadside Assistance',
      FireDamage: 'Fire Damage',
      WindscreenDamage: 'Windscreen Damage',
      TowingAndRecovery: 'Towing and Recovery',
      CarRental: 'Car Hire',
      CashBackIncluded: 'No Claim Bonus',
      IncludesDreadAndCriticalIllness: 'Includes Dread & Critical Illness',
      MedicalTestRequired: 'Medical Test Required',
      ImmediateFuneralPayout: 'Immediate Funeral Payout',
      //   CashBackIncluded: 'Cash Back Included',
    };

    let coverSummaryHeader = 'Cover Summary';
    if (category === 'Vehicle') {
      coverSummaryHeader = 'Cover Summary';
    } else if (category === 'Family') {
      switch (product) {
        case 'LifeInsurance':
          coverSummaryHeader = 'Product Features';
          break;
        case 'FuneralInsurance':
          coverSummaryHeader = 'Product Benefits';
          break;
      }
    }
    const coverageSummary = Object.entries(data).reduce((acc, [key, value]) => {
      if (coverageMap[key] && key !== 'CashBackIncluded') {
        acc.push({
          text: coverageMap[key],
          included: value,
        });
      }
      return acc;
    }, []);
    // case statement needed
    const derivedShortUSP = data.ShortUSP
      ? data.ShortUSP
      : category === 'Life'
        ? data.CoverType
        : '';
    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      return onBuyOnline(evt, { action: 'buy-online', data });
    };
    return (
      <Box
        ref={ref}
        sx={{
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          margin: { xs: '0 16px 16px 16px', sm: '0 0 16px 0' },
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '1px solid #2DC8CB' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          position: 'relative',
        }}
      >
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0, // makes it stretch across the box’s full width
              height: '40px',
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // center the text horizontally
              boxSizing: 'border-box',
              zIndex: 1, // ensure it stays above the rest
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.25rem', // 28px
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              {bestDealContentReady ? (
                'Your Best Deal!'
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box',
            mt: data.bestDeal && initialLoadDone ? '40px' : 0,
          }}
        >
          {/* Expand/Collapse Button */}
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              position: 'absolute',
              top: `${data.bestDeal && initialLoadDone ? 71 : 20}px`,
              right: '16px',
              width: '48px',
              height: '48px',
              padding: '8px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{
                fontWeight: 500,
                fontSize: '1.5rem', // Same size as before
                color: '#000',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
              }}
            >
              keyboard_arrow_down
            </span>
          </Button>

          {/* Logo / Image */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={LogoURL}
                alt={FriendlyName}
                style={{
                  maxHeight: '48px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* Provider Name */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                mt: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1rem', // 16px
                color: '#1E354E',
              }}
            >
              {FriendlyName}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Content */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '0.875rem', // 14px
                color: '#1E354E',
              }}
            >
              {derivedShortUSP}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Divider */}
          {data.bestDeal && initialLoadDone && (
            <Box
              sx={{
                mt: '12px',
                width: '100%',
                height: '1px',
                backgroundColor: '#E0E0E0',
              }}
            />
          )}

          {data.bestDeal && initialLoadDone && (
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Box sx={{ display: 'flex', mt: '8px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={getStarIcon(star, averageRating)}
                    alt="star"
                    style={{ width: '18px', height: '18px' }}
                  />
                ))}
              </Box>
            </SkeletonLoaderWrapper>
          )}

          {/* Additional Benefits */}
          {data.bestDeal && initialLoadDone && (
            <Box sx={{ mt: 1.5 }}>
              {[data.HeroBullet1, data.HeroBullet2].map(
                (bullet, i) =>
                  bullet && (
                    <Box
                      key={i}
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      {isBestDealLoading ? null : (
                        <span
                          className="material-symbols-rounded"
                          style={{
                            fontSize: '1.5rem',
                            color: '#1E354E',
                            marginRight: '8px',
                            fontWeight: 500,
                          }}
                        >
                          check
                        </span>
                      )}

                      <SkeletonLoaderWrapper loading={isBestDealLoading}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            color: '#1E354E',
                          }}
                        >
                          {bullet}
                        </Typography>
                      </SkeletonLoaderWrapper>
                    </Box>
                  )
              )}
            </Box>
          )}

          {/* Divider */}
          <Box
            sx={{
              mt: '12px',
              width: '100%',
              height: '1px',
              backgroundColor: '#E0E0E0',
            }}
          />

          {/* Additional Info Pill */}
          {initialLoadDone &&
            Array.isArray(data.RetailChips) &&
            data.RetailChips.length > 0 && (
              <Box
                sx={{
                  mt: '12px',
                  backgroundColor: data.RetailChipColor,
                  borderRadius: '4px',
                  px: '0px',
                  py: '0px',
                  display: 'inline-block',
                }}
              >
                {data.RetailChips.map((chip, idx) => (
                  <Box key={idx}>
                    <SkeletonLoaderWrapper loading={isBestDealLoading}>
                      <Box
                        key={idx}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#162F3B',
                          backgroundColor: chip.color,
                          letterSpacing: '-0.02em',
                          lineHeight: '1.125rem',
                          borderRadius: '6px',
                          px: '8px',
                          py: '2px',
                          display: 'inline-block',
                        }}
                      >
                        {chip.text}
                      </Box>
                    </SkeletonLoaderWrapper>
                  </Box>
                ))}
              </Box>
            )}

          {/* Bottom Section: Premium/Excess on top, CTA button full width underneath */}
          <Box
            sx={{
              mt:
                initialLoadDone && data.RetailChips.length > 0 ? '0px' : '12px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {/* Premium and Excess */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                }}
              >
                <RollingNumber
                  value={TotalPremium}
                  prefix={{
                    text: 'R',
                  }}
                  digitSx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '2rem',
                    color: '#162F3B',
                    letterSpacing: '-0.02em',
                    mt: '8px',
                    mb: '8px',
                  }}
                  suffix={{
                    text: 'pm',
                    sx: {
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      ml: '0px',
                      color: '#162F3B',
                    },
                  }}
                  wrapperSx={{}}
                  extraLoops={3}
                  playSignal={
                    data.bestDeal && wasLoadingOnMount.current ? 1 : 0
                  }
                  animate={data.bestDeal && wasLoadingOnMount.current}
                />
              </Box>
              {/* Excess or Cover Value */}
              {Excess && Excess !== '0' ? (
                <SkeletonLoaderWrapper loading={isBestDealLoading}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '1rem',
                      letterSpacing: '0.002em',
                      lineHeight: '24px',
                      color: '#A2ACB1',
                      marginTop: '-5px',
                    }}
                  >
                    Excess R{parseInt(Excess, 10).toLocaleString('en-ZA')}
                  </Typography>
                </SkeletonLoaderWrapper>
              ) : CoverValue && CoverValue !== '0' ? (
                <SkeletonLoaderWrapper loading={isBestDealLoading}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '1rem',
                      letterSpacing: '0.002em',
                      lineHeight: '24px',
                      color: '#A2ACB1',
                      marginTop: '-5px',
                    }}
                  >
                    R{parseInt(CoverValue, 10).toLocaleString('en-ZA')} Cover
                  </Typography>
                </SkeletonLoaderWrapper>
              ) : null}
            </Box>

            {/* CTA Button(s) — full width, underneath Premium/Excess */}
            {hasBuyNow ? (
              /* Two CTAs — Buy Now + I'm Interested (matches Travel Insurance) */
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <Button
                  onClick={handleBuyOnline}
                  disabled={buyNowLoading || !initialLoadDone}
                  aria-label="Buy Now"
                  sx={{
                    flex: '0 0 auto',
                    height: '48px',
                    minWidth: 0,
                    textTransform: 'none',
                    borderRadius: '18px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #FF0074',
                    boxShadow: '0px 1px 2px 0px #1018280D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '12px',
                    '&:hover': { backgroundColor: '#f4eeee' },
                  }}
                >
                  {buyNowLoading && buyNowLoadingForQuotePk === data.QuotePK ? (
                    <LoadingDots variant="buy-now-pink" />
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#FF0074',
                          mr: '4px',
                        }}
                      >
                        Buy
                      </Typography>
                      <Box
                        component="img"
                        src={BuyIconPink}
                        alt="Buy Now"
                        sx={{ width: '18px', height: '18px', display: 'block' }}
                      />
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSelectQuote}
                  disabled={buyNowLoading || isBestDealLoading}
                  sx={{
                    flex: '1 1 auto',
                    height: '48px',
                    backgroundColor: '#FF0074',
                    borderRadius: '18px',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '16px',
                    py: '8px',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#e60067' },
                    ...(isBestDealLoading && {
                      backgroundColor: '#D5D5D5 !important',
                      borderColor: '#D5D5D5 !important',
                      color: '#7A7A7A !important',
                      cursor: 'not-allowed',
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: '#D5D5D5 !important' },
                    }),
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '1rem',
                      lineHeight: '24px',
                      letterSpacing: '-0.02em',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    I&apos;m Interested
                    <span
                      className="material-symbols-rounded"
                      style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                    >
                      double_arrow
                    </span>
                  </Typography>
                </Button>
              </Box>
            ) : (
              /* Single CTA — no buy-now available */
              <Button
                onClick={handleSelectQuote}
                disabled={buyNowLoading}
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '48px',
                  backgroundColor: '#FF0074',
                  borderRadius: '18px',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: '16px',
                  py: '8px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#e60067' },
                  ...(isBestDealLoading && {
                    backgroundColor: '#D5D5D5 !important',
                    borderColor: '#D5D5D5 !important',
                    color: '#7A7A7A !important',
                    cursor: 'not-allowed',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#D5D5D5 !important',
                    },
                  }),
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '1rem',
                    lineHeight: '24px',
                    letterSpacing: '-0.02em',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  I&apos;m Interested
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                  >
                    double_arrow
                  </span>
                </Typography>
              </Button>
            )}
          </Box>

          {isExpanded && (
            <Box
              sx={{
                mt: '16px', // space above the expanded section
                display: 'flex',
                flexDirection: 'column',
                gap: '16px', // spacing between sections
              }}
            >
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* Description */}
              {data.LongUSP && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '1rem', // 16px
                      color: '#1E354E',
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '1rem', // 16px
                      color: '#1E354E',
                      mt: '8px',
                    }}
                  >
                    {data.LongUSP}
                  </Typography>
                </Box>
              )}
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* Coverage Summary */}
              {coverageSummary && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '1rem',
                      mb: '8px',
                      color: '#1E354E',
                    }}
                  >
                    {coverSummaryHeader}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '8px',
                    }}
                  >
                    {data.ProductBenefits
                      ? // --- Render ProductBenefits as single-column list ---
                        data.ProductBenefits.split(/<\/li>/)
                          .map((item) =>
                            item.replace(/<li>|<\/?[^>]+(>|$)/g, '').trim()
                          ) // clean HTML
                          .filter(Boolean)
                          .map((benefit, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <span
                                className="material-symbols-rounded"
                                style={{
                                  fontSize: '1.5rem', // 24px
                                  fontWeight: 500,
                                }}
                              >
                                check
                              </span>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter',
                                  fontWeight: 400,
                                  fontSize: '0.875rem', // 14px
                                  color: '#000000',
                                }}
                              >
                                {benefit}
                              </Typography>
                            </Box>
                          ))
                      : // --- Otherwise render coverageSummary list ---
                        coverageSummary.map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span
                              className="material-symbols-rounded"
                              style={{
                                fontSize: '1.5rem', // 24px
                                fontWeight: 500,
                              }}
                            >
                              {item.included === 'true' ||
                              item.included === 'True'
                                ? 'check'
                                : 'close'}
                            </span>
                            <Typography
                              sx={{
                                fontFamily: 'Inter',
                                fontWeight: 400,
                                fontSize: '0.875rem', // 14px
                                color: item.included ? '#000000' : '#B3B3B3',
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        ))}
                  </Box>
                </Box>
              )}

              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* Reviews */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '1rem',
                    mb: '4px',
                    color: '#1E354E',
                  }}
                >
                  Reviews
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {reviewData.map((review, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      {/* Logo */}
                      <img
                        src={review.logo}
                        alt={`${review.name} logo`}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'contain',
                        }}
                      />

                      {/* Stars */}
                      <Box sx={{ display: 'flex' }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <img
                            key={star}
                            src={getStarIcon(star, review.score)}
                            alt="star"
                            style={{
                              width: '18px',
                              height: '18px',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
MobileProviderTileComponent.displayName =
  'MobileProviderTileComponent_LifeInsurance';
export default MobileProviderTileComponent;
